import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ChevronDown, ChevronRight, Plus, X, ExternalLink, StickyNote, Loader2 } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";

const LAYERS = [
  {
    key: "planejamento_estrategico", title: "1. Planejamento Estratégico",
    fields: [
      { key: "objetivo", label: "Objetivo do trimestre", type: "textarea" },
      { key: "kpis", label: "KPIs monitorados", type: "list" },
      { key: "milestones", label: "Marcos importantes", type: "list" },
    ],
  },
  {
    key: "inteligencia_produtos", title: "2. Inteligência de Produtos",
    fields: [
      { key: "produtos", label: "Produtos/serviços em foco", type: "list" },
      { key: "beneficios", label: "Principais benefícios por produto", type: "textarea" },
      { key: "provas", label: "Provas sociais / cases", type: "list" },
    ],
  },
  {
    key: "repertorio", title: "3. Repertório da Marca / Pessoa",
    fields: [
      { key: "historias", label: "Histórias-chave que valem contar", type: "list" },
      { key: "obras", label: "Livros, filmes, referências", type: "list" },
      { key: "citacoes", label: "Frases/citações favoritas", type: "list" },
    ],
  },
  {
    key: "agendas_calendario", title: "4. Agendas & Calendário",
    fields: [
      { key: "eventos", label: "Eventos do setor", type: "list" },
      { key: "datas_comerciais", label: "Datas comerciais relevantes", type: "list" },
      { key: "campanhas", label: "Campanhas planejadas", type: "list" },
    ],
  },
  {
    key: "stakeholders_aprovacao", title: "5. Stakeholders & Aprovação",
    fields: [
      { key: "responsaveis", label: "Quem aprova o conteúdo", type: "textarea" },
      { key: "sla_aprovacao", label: "SLA de aprovação (dias)", type: "text" },
      { key: "restricoes", label: "Assuntos proibidos / restrições", type: "list" },
    ],
  },
  {
    key: "registros_reuniao", title: "6. Registros de Reunião",
    fields: [
      { key: "ata_ultima", label: "Última reunião (resumo)", type: "textarea" },
      { key: "decisoes", label: "Decisões pendentes", type: "list" },
      { key: "proxima", label: "Data da próxima reunião", type: "text" },
    ],
  },
  {
    key: "calendario_postagens", title: "7. Calendário de Postagens",
    fields: [
      { key: "frequencia", label: "Frequência semanal por canal", type: "textarea" },
      { key: "horarios", label: "Melhores horários", type: "list" },
      { key: "obs", label: "Observações", type: "textarea" },
    ],
  },
];

function ListInput({ value, onChange, placeholder, testId }) {
  const [tmp, setTmp] = useState("");
  const items = Array.isArray(value) ? value : [];
  return (
    <div>
      <div className="flex gap-2">
        <input className="input" data-testid={testId} value={tmp} placeholder={placeholder} onChange={(e) => setTmp(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && tmp.trim()) { e.preventDefault(); onChange([...items, tmp.trim()]); setTmp(""); } }} />
        <button type="button" onClick={() => { if (tmp.trim()) { onChange([...items, tmp.trim()]); setTmp(""); } }} className="btn-outline"><Plus size={16} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {items.map((it, i) => <span key={`${it}-${i}`} className="chip flex items-center gap-1">{it}<button onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-muted hover:text-[var(--error)]"><X size={11} /></button></span>)}
      </div>
    </div>
  );
}

function LayerCard({ layer, data, onSave }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(data || {});
  const debounceRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const initialized = useRef(false);

  // Only sync from props on the FIRST load — avoids clobbering typing during autosave refresh.
  useEffect(() => {
    if (!initialized.current && data) {
      setValues(data);
      initialized.current = true;
    }
  }, [data]);

  const setField = (k, v) => {
    const nv = { ...values, [k]: v };
    setValues(nv);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try { await onSave(nv); } finally { setSaving(false); }
    }, 700);
  };

  const completeness = Object.values(values || {}).filter((v) => v && (Array.isArray(v) ? v.length > 0 : true)).length;
  const percent = Math.round((completeness / layer.fields.length) * 100);

  return (
    <div className="card" data-testid={`layer-${layer.key}`}>
      <button onClick={() => setOpen(!open)} data-testid={`layer-toggle-${layer.key}`} className="w-full p-5 flex items-center gap-3 text-left">
        {open ? <ChevronDown size={18} className="text-muted" /> : <ChevronRight size={18} className="text-muted" />}
        <div className="flex-1">
          <div className="font-semibold">{layer.title}</div>
          <div className="text-xs text-muted mt-0.5">{completeness}/{layer.fields.length} campos preenchidos</div>
        </div>
        <div className="w-24">
          <div className="progress"><div style={{ width: `${percent}%` }} /></div>
        </div>
        <div className="text-sm text-muted w-10 text-right">{percent}%</div>
        {saving && <Loader2 size={14} className="animate-spin text-muted" />}
      </button>
      {open && (
        <div className="border-t p-5 space-y-4" style={{ borderColor: "var(--border)" }}>
          {layer.fields.map((f) => (
            <div key={f.key}>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">{f.label}</label>
              <div className="mt-1.5">
                {f.type === "textarea" && <textarea className="input" rows={3} data-testid={`wfield-${layer.key}-${f.key}`} value={values[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} />}
                {f.type === "text" && <input className="input" data-testid={`wfield-${layer.key}-${f.key}`} value={values[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} />}
                {f.type === "list" && <ListInput value={values[f.key]} onChange={(v) => setField(f.key, v)} placeholder="Digite e Enter" testId={`wfield-${layer.key}-${f.key}`} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SwipeFile({ items, onAdd, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ kind: "link", url: "", note: "", tags: "", mode: "passiva", status: "usar" });
  const [tab, setTab] = useState("all");

  const submit = async (e) => {
    e.preventDefault();
    await onAdd({ ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
    setForm({ kind: "link", url: "", note: "", tags: "", mode: "passiva", status: "usar" });
    setShowForm(false);
  };

  const filtered = items.filter((i) => tab === "all" || i.mode === tab);

  return (
    <div className="card p-5 space-y-4" data-testid="swipe-file">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// curadoria</div>
          <h3 className="text-lg font-bold">Swipe File</h3>
          <p className="text-xs text-muted">Salve referências (links ou notas) e classifique por passiva/ativa.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="swipe-new" className="btn-grad text-sm flex items-center gap-1"><Plus size={14} /> Nova referência</button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="card-2 p-4 space-y-3" data-testid="swipe-form">
          <div className="flex gap-2">
            {["link", "note"].map((k) => (
              <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })} data-testid={`swipe-kind-${k}`} className={`chip ${form.kind === k ? "grad" : ""}`}>{k === "link" ? "Link" : "Nota"}</button>
            ))}
          </div>
          {form.kind === "link" ? (
            <input className="input" data-testid="swipe-url" placeholder="https://…" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required />
          ) : (
            <textarea className="input" rows={2} data-testid="swipe-note" placeholder="Anote a ideia…" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} required />
          )}
          <input className="input" data-testid="swipe-tags" placeholder="Tags (separadas por vírgula)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <select className="input" data-testid="swipe-mode" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              <option value="passiva">Passiva</option>
              <option value="ativa">Ativa</option>
            </select>
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="usar">Usar</option>
              <option value="descartar">Descartar</option>
              <option value="arquivar">Arquivar</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-grad text-sm" data-testid="swipe-save">Salvar</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-outline text-sm" data-testid="swipe-cancel">Cancelar</button>
          </div>
        </form>
      )}

      <div className="flex gap-2">
        {["all", "passiva", "ativa"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`swipe-tab-${t}`} className={`chip ${tab === t ? "grad" : ""}`}>{t === "all" ? "Todas" : t}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm text-muted text-center py-6">Nenhuma referência ainda.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => (
            <div key={it.id} className="card-2 p-3 flex items-start gap-3" data-testid={`swipe-item-${it.id}`}>
              <div className="w-8 h-8 rounded-lg grad-bg-soft flex items-center justify-center flex-shrink-0">
                {it.kind === "link" ? <ExternalLink size={14} /> : <StickyNote size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                {it.kind === "link" ? (
                  <a href={it.url} target="_blank" rel="noreferrer" className="text-sm truncate block grad-text hover:underline">{it.url}</a>
                ) : (
                  <div className="text-sm">{it.note}</div>
                )}
                <div className="flex flex-wrap gap-1 mt-1">
                  {it.tags?.map((t) => <span key={t} className="chip text-[10px]" style={{ padding: "2px 6px" }}>{t}</span>)}
                  <span className="chip text-[10px]" style={{ padding: "2px 6px" }}>{it.mode}</span>
                  <span className="chip text-[10px]" style={{ padding: "2px 6px" }}>{it.status}</span>
                </div>
              </div>
              <button onClick={() => onDelete(it.id)} className="text-muted hover:text-[var(--error)]" data-testid={`swipe-delete-${it.id}`}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Workspace() {
  const { activeBrand } = useAuth();
  const [ws, setWs] = useState(null);

  const load = async () => {
    if (!activeBrand) return;
    const { data } = await api.get(`/workspace/${activeBrand.brand_id}`);
    setWs(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeBrand]);

  const saveLayer = async (layer_key, data) => {
    try {
      await api.put(`/workspace/${activeBrand.brand_id}/layer`, { layer_key, data });
      await load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const addSwipe = async (payload) => {
    try {
      await api.post(`/workspace/${activeBrand.brand_id}/swipe`, payload);
      await load();
      toast.success("Adicionado");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const delSwipe = async (id) => {
    try {
      await api.delete(`/workspace/${activeBrand.brand_id}/swipe/${id}`);
      await load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  if (!ws) return <div className="p-10 text-muted font-mono text-xs">carregando…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="workspace-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// módulo 1 · operação</div>
          <h1 className="text-3xl font-extrabold">Workspace</h1>
          <p className="text-muted text-sm mt-1">7 camadas + swipe file. Todas com auto-save.</p>
        </div>
        <div className="card-2 px-4 py-2.5">
          <div className="text-xs text-muted font-mono uppercase tracking-widest">Completude geral</div>
          <div className="flex items-center gap-2 mt-1">
            <div className="progress w-32"><div style={{ width: `${ws.progress.percent}%` }} /></div>
            <div className="font-bold text-sm">{ws.progress.percent}%</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-3">
          {LAYERS.map((l) => (
            <LayerCard key={l.key} layer={l} data={ws.layers?.[l.key]?.data} onSave={(d) => saveLayer(l.key, d)} />
          ))}
        </div>
        <SwipeFile items={ws.swipe_file || []} onAdd={addSwipe} onDelete={delSwipe} />
      </div>
    </div>
  );
}
